import { z } from "zod";

const stableIdSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Gebruik kleine letters, cijfers en koppeltekens.");

export const catalogueVersionSchema = z
  .object({
    id: z.string().min(1),
    versionNumber: z.number().int().positive(),
    status: z.enum(["draft", "published", "retired"]),
    revision: z.number().int().positive(),
    createdAt: z.string().datetime(),
    createdBy: z.string().min(1),
    publishedAt: z.string().datetime().nullable(),
  })
  .strict();

export const catalogueOptionDraftSchema = z
  .object({
    id: stableIdSchema,
    label: z.string().trim().min(1).max(300),
    correct: z.boolean(),
  })
  .strict();

export const catalogueQuestionDraftSchema = z
  .object({
    id: stableIdSchema,
    prompt: z.string().trim().min(1).max(500),
    explanation: z.string().trim().max(1_000),
    options: z.array(catalogueOptionDraftSchema).min(2),
  })
  .strict();

export const catalogueQuizDraftSchema = z
  .object({
    id: stableIdSchema,
    title: z.string().trim().min(1).max(200),
    passMark: z.number().int().min(1).max(100),
    questions: z.array(catalogueQuestionDraftSchema).min(1),
  })
  .strict();

export const catalogueRoleDraftSchema = z
  .object({
    id: stableIdSchema,
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(2_000),
    skillIds: z.array(stableIdSchema).min(1),
    archived: z.boolean(),
  })
  .strict();

export const catalogueSkillDraftSchema = z
  .object({
    id: stableIdSchema,
    title: z.string().trim().min(1).max(200),
    shortTitle: z.string().trim().min(1).max(80),
    description: z.string().trim().min(1).max(2_000),
    category: z.string().trim().min(1).max(100),
    icon: z.string().trim().min(1).max(20),
    archived: z.boolean(),
    assessment: catalogueQuizDraftSchema,
    test: catalogueQuizDraftSchema,
  })
  .strict();

export const catalogueTrainingSectionDraftSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    body: z.string().trim().min(1).max(5_000),
    tip: z.string().trim().min(1).max(1_000).optional(),
  })
  .strict();

export const catalogueTrainingDraftSchema = z
  .object({
    id: stableIdSchema,
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(2_000),
    minutes: z.number().int().positive().max(480),
    skillIds: z.array(stableIdSchema).min(1),
    sections: z.array(catalogueTrainingSectionDraftSchema).min(1),
    archived: z.boolean(),
  })
  .strict();

export const catalogueGraphSchema = z
  .object({
    roles: z.array(catalogueRoleDraftSchema),
    skills: z.array(catalogueSkillDraftSchema),
    trainings: z.array(catalogueTrainingDraftSchema),
  })
  .strict();

export const catalogueDraftSchema = z
  .object({ version: catalogueVersionSchema, graph: catalogueGraphSchema })
  .strict();

export const catalogueOverviewSchema = z
  .object({
    published: catalogueVersionSchema,
    draft: catalogueVersionSchema.nullable(),
    history: z.array(catalogueVersionSchema),
  })
  .strict();

export const replaceCatalogueDraftSchema = z
  .object({ expectedRevision: z.number().int().positive(), graph: catalogueGraphSchema })
  .strict();

export const catalogueRevisionSchema = z.object({ expectedRevision: z.number().int().positive() }).strict();

export const catalogueRevisionConflictSchema = z
  .object({
    error: z.string().min(1),
    expectedRevision: z.number().int().positive(),
    currentRevision: z.number().int().positive(),
  })
  .strict();

export const catalogueFieldErrorSchema = z
  .object({
    path: z.string().min(1),
    code: z.string().min(1),
    message: z.string().min(1),
  })
  .strict();

export const catalogueValidationSchema = z
  .object({ valid: z.boolean(), errors: z.array(catalogueFieldErrorSchema) })
  .strict();

const entityImpactSchema = z
  .object({
    added: z.array(stableIdSchema),
    changed: z.array(stableIdSchema),
    archived: z.array(stableIdSchema),
  })
  .strict();

export const cataloguePublicationImpactSchema = z
  .object({
    roles: entityImpactSchema,
    skills: entityImpactSchema,
    trainings: entityImpactSchema,
    remappedRoles: z.array(stableIdSchema),
    remappedTrainings: z.array(stableIdSchema),
    affectedEmployees: z.array(
      z
        .object({
          employeeId: z.string().min(1),
          roleId: stableIdSchema,
          newSkillIds: z.array(stableIdSchema),
        })
        .strict(),
    ),
  })
  .strict();

export const cataloguePublicationResultSchema = z
  .object({ version: catalogueVersionSchema, initializedProgress: z.number().int().nonnegative() })
  .strict();

export const historicalCatalogueReferenceSchema = z
  .object({
    versionId: z.string().min(1),
    versionNumber: z.number().int().positive(),
    displayName: z.string().min(1),
  })
  .strict();

export type CatalogueVersion = z.infer<typeof catalogueVersionSchema>;
export type CatalogueGraph = z.infer<typeof catalogueGraphSchema>;
export type CatalogueDraft = z.infer<typeof catalogueDraftSchema>;
export type CatalogueFieldError = z.infer<typeof catalogueFieldErrorSchema>;
export type CatalogueValidation = z.infer<typeof catalogueValidationSchema>;
export type CataloguePublicationImpact = z.infer<typeof cataloguePublicationImpactSchema>;
export type HistoricalCatalogueReference = z.infer<typeof historicalCatalogueReferenceSchema>;
